// packages/web/src/components/ClassroomsList.tsx — misma lógica (useAsync + paginación), nueva presentación.
import { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, RefreshCw } from 'lucide-react';
import { api } from '../api';
import { useAsync } from '../hooks/useAsync';
import { Alert, Button, Card, PageHeader, StatusBadge, TABLE, shortDate } from './ui';

interface Props {
  onNew: () => void;
  onOpenLive: (classroomId: string) => void;
  onClose: () => void;
}

const PAGE_SIZE = 20;

export function ClassroomsList({ onNew, onOpenLive, onClose }: Props) {
  const [refreshTick, setRefreshTick] = useState(0);
  const [page, setPage] = useState(0);
  const state = useAsync(
    () => api.listClassrooms({ limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    [refreshTick, page],
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader
        back={{ label: 'Volver', onClick: onClose }}
        title="Mis aulas"
        subtitle="Abre un aula para seguir el avance de cada alumno en vivo."
        actions={<Button onClick={onNew} leftIcon={<Plus aria-hidden className="w-4 h-4" />}>Nueva aula</Button>}
      />

      {state.loading && <TableSkeleton />}
      {state.error && <Alert kind="error">{state.error}</Alert>}

      {state.loading === false && state.data && state.data.classrooms.length === 0 && (
        <Card>
          <div className="flex flex-col items-start gap-3 py-4">
            <p className="text-slate-700">Todavía no creaste aulas.</p>
            <Button onClick={onNew} leftIcon={<Plus aria-hidden className="w-4 h-4" />}>Crear la primera aula</Button>
          </div>
        </Card>
      )}

      {state.loading === false && state.data && state.data.classrooms.length > 0 && (
        <div className={TABLE.wrap}>
          <table className={TABLE.table}>
            <caption className="sr-only">Aulas creadas, de la más reciente a la más antigua</caption>
            <thead className={TABLE.thead}>
              <tr>
                <th scope="col" className={TABLE.th}>Aula</th>
                <th scope="col" className={TABLE.th + ' w-[28%]'}>Avance</th>
                <th scope="col" className={TABLE.th}>Creada</th>
                <th scope="col" className={TABLE.th}>Estado</th>
                <th scope="col" className={TABLE.th}><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody>
              {state.data.classrooms.map((c) => {
                const pct = c.pilot_count > 0 ? Math.round((c.pilots_closed / c.pilot_count) * 100) : 0;
                return (
                  <tr key={c.id} className={TABLE.tr}>
                    <td className={TABLE.td}>
                      <div className="font-semibold text-slate-900">{c.name}</div>
                      <div className="text-[13px] text-slate-600">{c.level_name} · {c.oa_code}</div>
                    </td>
                    <td className={TABLE.td}>
                      <div className="flex flex-col gap-1.5">
                        <div role="meter" aria-valuemin={0} aria-valuemax={c.pilot_count} aria-valuenow={c.pilots_closed}
                          aria-label={`${c.pilots_closed} de ${c.pilot_count} terminaron`} className="h-1.5 rounded-full bg-slate-100">
                          <div className="h-1.5 rounded-full bg-emerald-600" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[13px] text-slate-700">
                          <strong className="font-semibold">{c.pilots_closed} de {c.pilot_count}</strong> terminaron
                          <span className="text-slate-600"> · {c.pilots_started} iniciaron</span>
                        </span>
                      </div>
                    </td>
                    <td className={TABLE.td + ' text-slate-700 whitespace-nowrap'}>
                      <time dateTime={c.created_at} title={new Date(c.created_at).toLocaleString('es-CL')}>{shortDate(c.created_at)}</time>
                    </td>
                    <td className={TABLE.td}>
                      {c.closed_at ? <StatusBadge tone="muted">Cerrada</StatusBadge> : <StatusBadge tone="ok">Activa</StatusBadge>}
                    </td>
                    <td className={TABLE.td + ' text-right'}>
                      <Button variant="secondary" size="sm" onClick={() => onOpenLive(c.id)} aria-label={`Abrir ${c.name}`}>Abrir</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {state.loading === false && state.data && (
        <Pagination page={page} pageSize={PAGE_SIZE} total={state.data.total} onChange={setPage}
          onRefresh={() => setRefreshTick((t) => t + 1)} />
      )}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className={TABLE.wrap + ' p-4 space-y-3'} aria-busy="true" aria-label="Cargando aulas">
      {[0, 1, 2, 3].map((i) => <div key={i} className="h-10 rounded-md bg-slate-100 animate-pulse" />)}
    </div>
  );
}

function Pagination({ page, pageSize, total, onChange, onRefresh }: {
  page: number; pageSize: number; total: number; onChange: (page: number) => void; onRefresh: () => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(total, (page + 1) * pageSize);
  return (
    <nav aria-label="Paginación" className="flex items-center justify-between flex-wrap gap-3">
      <div className="text-[13px] text-slate-600">
        {total === 0 ? 'Sin aulas' : `${start}–${end} de ${total} aula${total === 1 ? '' : 's'}`}
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onRefresh} leftIcon={<RefreshCw aria-hidden className="w-4 h-4" />}>Actualizar</Button>
        <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => onChange(page - 1)} leftIcon={<ChevronLeft aria-hidden className="w-4 h-4" />}>Anterior</Button>
        <span className="text-[13px] text-slate-600 tabular-nums px-1">{page + 1} / {pages}</span>
        <Button variant="secondary" size="sm" disabled={page + 1 >= pages} onClick={() => onChange(page + 1)} rightIcon={<ChevronRight aria-hidden className="w-4 h-4" />}>Siguiente</Button>
      </div>
    </nav>
  );
}
